import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PendingBanner } from "@/features/presence/components/pending-banner";
import type { PeriodKind } from "@/features/presence/types";
import { pendingDateParam } from "@/features/presence/utils/pending";
import { periodRange } from "@/features/presence/utils/period";
import { AttendanceSheet } from "@/features/team/components/attendance-sheet";
import { InviteDialog } from "@/features/team/components/invite-dialog";
import { InvitesList } from "@/features/team/components/invites-list";
import { MembersByRole } from "@/features/team/components/members-by-role";
import { TeamKpis } from "@/features/team/components/team-kpis";
import { NewWorkerButton } from "@/features/team/components/worker-dialog";
import { useInvites } from "@/features/team/hooks/use-invites";
import { activeWorkers, useWorkers } from "@/features/team/hooks/use-workers";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { currentWorkDay, dateOnlyToDate } from "@/lib/format";

function summary(workerCount: number, inviteCount: number): string {
	const members = `${workerCount} ${workerCount === 1 ? "pessoa" : "pessoas"}`;
	const invites = `${inviteCount} ${inviteCount === 1 ? "convite pendente" : "convites pendentes"}`;
	return `${members} · ${invites}`;
}

export function TeamPage() {
	const { data: workers } = useWorkers();
	const { data: invites } = useInvites();
	const { has } = usePermissions();
	const [searchParams] = useSearchParams();
	const canListTeam = has("time_entries:list_team");
	const canUpdateEntries = has("time_entries:update");
	const focusDate = pendingDateParam(searchParams);
	const [period, setPeriod] = useState<PeriodKind>("week");
	const [reference, setReference] = useState(
		() => dateOnlyToDate(focusDate) ?? currentWorkDay(),
	);

	useEffect(() => {
		const day = dateOnlyToDate(focusDate);
		if (day) setReference(day);
	}, [focusDate]);

	return (
		<div className="team-page">
			<div className="page-header">
				<div>
					<h1 className="page-title">Equipe</h1>
					<p className="page-subtitle">
						{summary(activeWorkers(workers).length, invites?.length ?? 0)}
					</p>
				</div>
				<div className="page-header__actions">
					<NewWorkerButton />
					<InviteDialog />
				</div>
			</div>

			<PendingBanner />

			<TeamKpis range={periodRange(period, reference)} />

			<MembersByRole />

			{canListTeam && (
				<AttendanceSheet
					canUpdate={canUpdateEntries}
					period={period}
					reference={reference}
					focusDate={focusDate}
					onPeriodChange={setPeriod}
					onReferenceChange={setReference}
				/>
			)}

			<Card>
				<CardHeader>
					<CardTitle>Convites pendentes</CardTitle>
				</CardHeader>
				<CardContent>
					<InvitesList />
				</CardContent>
			</Card>
		</div>
	);
}
